<template>
  <div
    ref="search"
    class="search-container"
  >
    <searchbar context="widget" />
    <div
      v-if="showSuggestedSearches"
      class="suggested-search-container"
    >
      <div class="header-container">
        <img
          v-if="suggestedSearchIcon"
          :src="suggestedSearchIcon.url"
          class="suggested-search-icon"
          alt=""
          role="presentation"
        />
        <hlt-text
          :inline="true"
          :size="'sm'"
          :variant="'content-secondary'"
        >
          {{ suggestedSearchLabel }}
        </hlt-text>
      </div>
      <div class="action-container">
        <button
          v-for="searchTerm in suggestedSearchTerms"
          :key="searchTerm"
          class="suggested-search"
          @click="handleSuggestedSearch(searchTerm)"
        >
          {{ searchTerm }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';

import HltText from '@/components/ui/text.vue';
import Searchbar from '@components/singletons/searchbar.vue';

export default {
  components: { HltText, Searchbar },
  props: {
    config: {
      type: Object,
    },
    dashboard: {
      type: Object,
    },
  },
  data() {
    return {
      scrollFn: () => {},
      showSuggestions: false,
    };
  },
  computed: {
    attachListeners() {
      // We only need to worry about the listeners if we are on a route that has a disappearingSearchbar meta attribute
      return this.$route.meta.disappearingSearchbar;
    },
    jsonConfig() {
      return this.config?.config;
    },
    suggestedSearchIcon() {
      if (this.suggestedSearchIconId) {
        return this.$store.getters['attachments/get'][this.suggestedSearchIconId];
      }
    },
    suggestedSearchIconId() {
      return this.jsonConfig?.suggestedSearchIconAttachmentId;
    },
    suggestedSearchLabel() {
      return this.jsonConfig?.suggestedSearchLabel || 'Trending Searches';
    },
    suggestedSearchTerms() {
      return this.jsonConfig?.suggestedSearchTerms;
    },
    showSuggestedSearches() {
      return this.showSuggestions && this.suggestedSearchTerms?.length > 0;
    },
  },
  mounted() {
    if (this.attachListeners) {
      this.checkPosition();
      this.scrollFn = debounce(this.checkPosition, 100);
      window.addEventListener('scroll', this.scrollFn);
    }
    this.setShowSuggestions();
  },
  beforeUnmount() {
    if (this.attachListeners) {
      window.removeEventListener('scroll', this.scrollFn);
    }
  },
  methods: {
    checkPosition() {
      const el = this.$refs.search?.getBoundingClientRect();
      if (!el) return;
      if (el.top < 0) {
        this.$store.commit('search/setSearchbarVisibility', false);
      } else {
        this.$store.commit('search/setSearchbarVisibility', true);
      }
    },
    handleSuggestedSearch(searchTerm) {
      const params = {
        dashboard: this.dashboard,
        query: searchTerm,
      };
      this.$analytics.events.completedSuggestedSearch(params);

      this.$router.push({
        name: 'search#index',
        query: {
          ...this.currentQuery,
          q: searchTerm.toLowerCase(),
        },
      });
    },
    setShowSuggestions() {
      if (this.jsonConfig) {
        let show = false;
        const user = this.$store.getters['user/get'];
        if (user?.previewer) {
          show = true;
        } else {
          const apptimizeEnabled = this.$apptimize.checkExperiments({
            experimentName: 'Suggested Searches',
            bool: 'showSuggestedSearches',
            defaultVal: false,
            matcher: /Suggested Searches/,
          });
          show = apptimizeEnabled;
        }

        this.showSuggestions = show;

        if (show && this.suggestedSearchIconId) {
          this.$store.dispatch('attachments/fetch', { ids: [this.suggestedSearchIconId] });
        }
      }
    },
  },
};
</script>

<style lang='scss' scoped>
.search-container {
  @include padded-widget;
  min-height: 40px;
  position: relative;

  .suggested-search-container {
    margin-top: 56px;
    padding: 48px 16px 0 !important;

    .header-container {
      align-items: center;
      display: flex;

      .suggested-search-icon {
        margin-right: 8px;
        width: 16px;
      }
    }

    .action-container {
      margin-top: 12px;

      @include breakpoint(small) {
        margin-left: -32px;
        margin-right: -32px;
        overflow-x: auto;
        white-space: nowrap;

        .suggested-search:first-child {
          margin-left: 32px;
        }
      }

      .suggested-search {
        background: #ffffff;
        border-radius: 24px;
        border: 2px solid #000000;
        color: #000000;
        display: inline-block;
        font-size: rem-calc(14);
        font-weight: $semi-bold;
        margin-right: 12px;
        padding: 8px 16px;

        &:hover {
          background: #000000;
          color: #ffffff;
        }
      }


    }
  }
}
</style>
